"use client"

import { useMemo } from "react"
import { useUserInfo } from "../../useUserInfo"
import { useAuth } from "../../useAuth"
import { ROLE } from "@tc/ui-permission/utils"
import { useAllFacetValues } from "@tc/ui-typesense-client"
import { useRegistrationDetails } from "./useRegistrationDetails"

export type StatusReportContext = "exports" | "transfers"

export interface OrganisationOption {
  label: string
  value: string
  registrationId: string
}

export interface UseStatusReportDownloadConfig {
  collectionName: string
  attribute: string
  context: StatusReportContext
  enabled: boolean
}

export interface UseStatusReportDownloadResult {
  organisationOptions: OrganisationOption[]
  isLoading: boolean
  isMPIAgent: boolean
}

/**
 * Fetches organisation options for status report download modals.
 * For MPI Agents, fetches all organisations from Typesense facets.
 * For regular users, uses their assigned registrations.
 */
export const useStatusReportDownload = ({
  collectionName,
  attribute,
  context,
  enabled,
}: UseStatusReportDownloadConfig): UseStatusReportDownloadResult => {
  const { registrationArray, loading: userInfoLoading } = useUserInfo()

  const { authUser } = useAuth()
  const isMPIAgent = authUser?.roles?.includes(ROLE.MPIAgent) ?? false

  const { values: allOrganisationIds, loading: facetsLoading } =
    useAllFacetValues({
      collectionName,
      attribute,
      context,
      enabled: isMPIAgent && enabled,
    })

  const registrationIdsToLookup = useMemo(() => {
    if (isMPIAgent) {
      return allOrganisationIds
    }
    return registrationArray
  }, [isMPIAgent, allOrganisationIds, registrationArray])

  const { registrations, loading: registrationLoading } =
    useRegistrationDetails(registrationIdsToLookup, context)

  const organisationOptions: OrganisationOption[] = useMemo(
    () =>
      registrations.map((reg) => ({
        label: reg.label,
        value: reg.id,
        registrationId: reg.info?.id ?? reg.id,
      })),
    [registrations],
  )

  const isLoading = userInfoLoading || facetsLoading || registrationLoading

  return { organisationOptions, isLoading, isMPIAgent }
}
